// I18nImportLogDialog.tsx
import * as React from "react";
import { useMemo } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid2 as Grid,
    Paper,
    Typography
} from "@mui/material";
import { CheckCircleOutline as CheckCircleOutlineIcon, ErrorOutline as ErrorOutlineIcon } from "@mui/icons-material";
import { ColumnDef } from "@tanstack/react-table";
import { getI18n, useTranslation } from "react-i18next";
import { AdminI18N, CommonI18N } from "@/translations";
import { DataTable } from "@/lib/components/DataTable";
import { formatDateTime, formatInteger } from "@/lib/utils";
import { I18NImportFindingDTO, I18NImportLogDTO } from "@/api/data-contracts";
import { languageLabelKeys } from "./languageLabels";

const severityLabelKeys: Record<"ERROR" | "WARNING", string> = {
    ERROR: AdminI18N.languageFiles.severity.ERROR,
    WARNING: AdminI18N.languageFiles.severity.WARNING
};

/**
 * `DataTable` requires `TData extends { id?: number }`. `I18NImportFindingDTO` declares no `id`
 * field at all — same weak-type-detection issue as `LanguageStatusRow` in
 * `LanguageFileManagementPage.tsx` (see the comment there for the full explanation).
 */
type FindingRow = I18NImportFindingDTO & { id?: number };

interface I18nImportLogDialogProps {
    open: boolean;
    onClose: () => void;
    importLog: I18NImportLogDTO;
}

const I18nImportLogDialog: React.FC<I18nImportLogDialogProps> = ({ open, onClose, importLog }) => {
    const { t } = useTranslation();
    const locale = getI18n().language;
    const findings: FindingRow[] = importLog.findings ?? [];

    const findingColumns: ColumnDef<FindingRow, unknown>[] = useMemo(
        () => [
            {
                header: t(AdminI18N.languageFiles.finding.rowNumber),
                accessorFn: (row: FindingRow) => row.rowNumber ?? "-",
                id: "rowNumber"
            },
            {
                header: t(AdminI18N.languageFiles.finding.key),
                accessorFn: (row: FindingRow) => row.key ?? "-",
                id: "key"
            },
            {
                header: t(AdminI18N.languageFiles.finding.message),
                accessorFn: (row: FindingRow) => row.message ?? "-",
                id: "message"
            },
            {
                header: t(AdminI18N.languageFiles.finding.severity),
                accessorFn: (row: FindingRow) => (row.severity ? t(severityLabelKeys[row.severity]) : "-"),
                id: "severity"
            }
        ],
        [t]
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {t(AdminI18N.languageFiles.dialog.importLogTitle)}
                {importLog.success ? (
                    <CheckCircleOutlineIcon color="success" sx={{ ml: 1 }} />
                ) : (
                    <ErrorOutlineIcon color="error" sx={{ ml: 1 }} />
                )}
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="h6">{t(AdminI18N.languageFiles.dialog.infoTitle)}</Typography>
                        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography variant="body2" color="textSecondary">
                                        {t(AdminI18N.languageFiles.column.language)}
                                    </Typography>
                                    <Typography variant="body1">
                                        {importLog.language ? t(languageLabelKeys[importLog.language]) : "-"}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography variant="body2" color="textSecondary">
                                        {t(AdminI18N.languageFiles.column.lastImportDate)}
                                    </Typography>
                                    <Typography variant="body1">
                                        {importLog.createdAt
                                            ? formatDateTime(new Date(importLog.createdAt), locale)
                                            : "-"}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography variant="body2" color="textSecondary">
                                        {t(AdminI18N.languageFiles.column.lastImportUser)}
                                    </Typography>
                                    <Typography variant="body1">
                                        {importLog.createdBy
                                            ? `${importLog.createdBy.firstName ?? ""} ${importLog.createdBy.lastName ?? ""}`.trim()
                                            : "-"}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography variant="body2" color="textSecondary">
                                        {t(AdminI18N.languageFiles.column.entryCount)}
                                    </Typography>
                                    <Typography variant="body1">
                                        {formatInteger(importLog.entryCount, locale) ?? 0}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography variant="body2" color="textSecondary">
                                        {t(AdminI18N.languageFiles.dialog.warningCount)}
                                    </Typography>
                                    <Typography variant="body1">
                                        {formatInteger(importLog.warningCount, locale) ?? 0}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <Typography variant="h6">{t(AdminI18N.languageFiles.dialog.findingsTitle)}</Typography>
                        <DataTable
                            columns={findingColumns}
                            data={findings}
                            noResultsValue={t(AdminI18N.languageFiles.finding.noFindings)}
                        />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="h6">
                            {t(AdminI18N.languageFiles.dialog.overallStatusTitle)}:{" "}
                            <Typography component="span" color={importLog.success ? "success.main" : "error.main"}>
                                {importLog.success
                                    ? t(AdminI18N.languageFiles.message.importSuccess)
                                    : t(AdminI18N.languageFiles.message.importFailed)}
                            </Typography>
                        </Typography>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained" color="primary">
                    {t(CommonI18N.action.close)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default I18nImportLogDialog;
